import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import useApiCaller from '../hooks/use-api-caller';
import { ICompanySummary, IUserListRef } from '../types/company';
import Loading from './Loading';
import PortfolioList from './PortfolioList';
import FSSError from './FSSError';
import CompareGraph from './CompareGraph';
import CompareTime from './CompareTime';
import CompareFilters from './CompareFilters';
import { ComparisonEnum } from '../constants/compare';
import { getUniqueCountries, getUniqueIndustries, areUserListsInCommon } from '../utils/compare-functions';
import { companiesErrorAtom, companiesAtom, compareSelectedAtom, loadingCompaniesAtom, userListsAtom } from '../store/portfolio';
import { countriesOptionsAtom, filterCountriesAtom, filterIndustriesAtom, filterListsAtom, industriesOptionsAtom } from '../store/filters';
import CompareSelector from './CompareSelector';
import { pageDateRangeOptions } from '../store/timerange';
import { companyHistoryIdsAtom, companyHistoryLoadingAtom, companyScoreHistoryAtom } from '../store/company';
import TimeFilterModal from './TimeFilterModal';
import { getTimeFilterModalPreference } from '../utils/local-storage';

export default function Companies({
  defaultList,
}: Readonly<{
  defaultList: number[];
}>) {
  const apiCaller = useApiCaller();

  const loadingCompanies = useRecoilValue<boolean>(loadingCompaniesAtom);
  const companiesError = useRecoilValue<boolean>(companiesErrorAtom);
  const companies = useRecoilValue<ICompanySummary[]>(companiesAtom);
  const userLists = useRecoilValue<IUserListRef[]>(userListsAtom);

  const [comparisonSelector, setComparisonSelector] = useRecoilState(compareSelectedAtom);

  const [filterLists, setFilterLists] = useRecoilState(filterListsAtom);
  const [filterIndustries, setFilterIndustries] = useRecoilState(filterIndustriesAtom);
  const [filterCountries, setFilterCountries] = useRecoilState(filterCountriesAtom);

  useEffect(() => {
    if (defaultList) {
      setFilterLists(defaultList);
    } else {
      setFilterLists([]);
    }
  }, defaultList);

  const [filteredCompanies, setFilteredCompanies] = useState<ICompanySummary[]>([]);

  const [industriesOptions, setIndustriesOptions] = useRecoilState(industriesOptionsAtom);
  const [countriesOptions, setcountriesOptions] = useRecoilState(countriesOptionsAtom);

  const setCompanyScoreHistory = useSetRecoilState(companyScoreHistoryAtom);
  const [historyLoading, setHistoryLoading] = useRecoilState(companyHistoryLoadingAtom);

  // ----------------------------------------------------------------------------
  // history
  const [isTimeFilterModalOpen, setIsTimeFilterModalOpen] = useState(false);
  const shouldDisplayTimeFilterModal = getTimeFilterModalPreference();
  const setCompanyHistoryIds = useSetRecoilState(companyHistoryIdsAtom);

  const getCompanyScoreHistory = async (abortController: AbortController) => {
    setHistoryLoading(true);
    const ids = filteredCompanies.map((company) => company.company_code).join(',');
    setCompanyHistoryIds(ids);
    const earliestDate = new Date(pageDateRangeOptions[0].earliestDate);
    earliestDate.setDate(earliestDate.getDate() - 1);

    try {
      const res = await apiCaller.getPortfolioWithHistory(
        ids,
        earliestDate.toISOString().split('T')[0],
        pageDateRangeOptions[0].latestDate,
        { signal: abortController.signal } // Attach the abort signal
      );
      // set start date for timeseries (this is not returned by the API)
      res.forEach((company) => (company.timeseriesStartDate = new Date(earliestDate)));
      setCompanyScoreHistory(res);
      setHistoryLoading(false);
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        setCompanyScoreHistory([]);
        setHistoryLoading(false);
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();

    if (comparisonSelector === ComparisonEnum.Time) {
      const compNum = filteredCompanies.length;
      // Fetch history
      if (!!compNum) {
        getCompanyScoreHistory(abortController);

        // Display helper modal
        if (compNum > 100 && !shouldDisplayTimeFilterModal) {
          setIsTimeFilterModalOpen(true);
        }
      } else {
        setCompanyScoreHistory([]);
      }
    }

    // Cleanup function to cancel the ongoing request
    return () => {
      abortController.abort();
    };
  }, [filteredCompanies, comparisonSelector]);

  // ----------------------------------------------------------------------------
  // filtering
  useEffect(() => {
    const filteredCompaniesLocal = companies
      .filter((company) => (filterLists.length > 0 ? areUserListsInCommon(filterLists, companies, company.company_code) : true))
      .filter((company) => (filterIndustries.length > 0 ? filterIndustries.includes(company.industry_sector) : true))
      .filter((company) => (filterCountries.length > 0 ? filterCountries.includes(company.country) : true));
    setFilteredCompanies(filteredCompaniesLocal);
  }, [companies, filterLists, filterCountries, filterIndustries]);

  useEffect(() => {
    setcountriesOptions(getUniqueCountries(companies, defaultList?.[0] ?? null));
    setIndustriesOptions(getUniqueIndustries(companies, defaultList?.[0] ?? null));
  }, [companies]);

  // ----------------------------------------------------------------------------
  useEffect(() => {
    // On mount
    setComparisonSelector(ComparisonEnum.Table);

    // On unmount
    return () => {
      // Reset filters
      setFilterCountries([]);
      setFilterIndustries([]);
      setFilterLists([]);
    };
  }, []);

  return (
    <div>
      {loadingCompanies && <Loading />}
      {companiesError && <FSSError />}
      {!loadingCompanies && !companiesError && companies.length !== 0 && (
        <div className="companies-list-container">
          <div className="hidden lg:flex mb-4 justify-between flex-row-reverse">
            <div className="min-w-[2rem] sm:min-w-[18.5rem] text-left pt-1.5">
              <CompareSelector />
            </div>
            <div className={comparisonSelector === ComparisonEnum.Table ? 'opacity-0' : ''}>
              <CompareFilters industries={industriesOptions} countries={countriesOptions} hideList={defaultList.length > 0} />
            </div>
            {!defaultList.length && comparisonSelector === ComparisonEnum.Table && (
              <h2 className="text-ow-primary text-3xl tracking-tight">Companies</h2>
            )}
          </div>
          <div>
            <div>
              {comparisonSelector === ComparisonEnum.Table && (
                <PortfolioList filteredCompanies={filteredCompanies} hideList={defaultList.length > 0} />
              )}
              {comparisonSelector === ComparisonEnum.Graph && (
                <CompareGraph
                  filteredCompanies={filteredCompanies}
                  allCompanies={companies}
                  list={defaultList[0] ? (userLists.find((list) => list.id === defaultList[0])?.name ?? '') : ''}
                />
              )}
              {comparisonSelector === ComparisonEnum.Time && historyLoading && (
                <div className="min-h-[500px] flex items-center justify-center">
                  <div className="flex flex-col font-bold">
                    <Loading />
                    <div className="mt-4">History loading...</div>
                  </div>
                </div>
              )}
              {comparisonSelector === ComparisonEnum.Time && !historyLoading && (
                <CompareTime list={defaultList[0] ? (userLists.find((list) => list.id === defaultList[0])?.name ?? '') : ''} />
              )}
            </div>
          </div>
        </div>
      )}
      <TimeFilterModal isOpen={isTimeFilterModalOpen} onClose={() => setIsTimeFilterModalOpen(false)} />
      {!loadingCompanies && !companiesError && !companies.length && (
        <div>
          <h2 className="text-ow-primary text-3xl tracking-tight pt-0.5 mb-6">Companies</h2>
          <p className="text-xs text-left">
            There are no companies in your portfolio yet. To add a company, use the Search field in the top bar and then click the green "+"
            symbol to add your first company. If you need more assistance, please{' '}
            <NavLink to="/contact" className="text-ow-secondary">
              contact us
            </NavLink>{' '}
            .
          </p>
        </div>
      )}
    </div>
  );
}
